import { useCallback, useRef } from 'react';
import { fetchAvailableZonesCached } from './utils';
import { MarketAggregatesState } from './types';

export const useAvailableZonesOperations = (
  state: MarketAggregatesState,
  setState: React.Dispatch<React.SetStateAction<MarketAggregatesState>>,
) => {
  const currentWeightTypeRef = useRef<string | null>(null);
  const pendingRequestRef = useRef<Promise<void> | null>(null);

  const fetchAvailableZones = useCallback(
    async (weightType: 'population' | 'wind_capacity' | 'solar_capacity') => {
      // Don't fetch if already loading the same weight type
      if (currentWeightTypeRef.current === weightType && pendingRequestRef.current) {
        return pendingRequestRef.current;
      }

      currentWeightTypeRef.current = weightType;

      setState((prev) => ({
        ...prev,
        availableZonesLoading: true,
      }));

      const request = (async () => {
        try {
          const zones = await fetchAvailableZonesCached(weightType);
          setState((prev) => ({
            ...prev,
            availableZones: zones,
            availableZonesLoading: false,
          }));
        } catch (error) {
          console.error('Failed to fetch available zones:', error);
          setState((prev) => ({
            ...prev,
            availableZones: [], // Fall back to empty array on error
            availableZonesLoading: false,
          }));
        } finally {
          pendingRequestRef.current = null;
          currentWeightTypeRef.current = null;
        }
      })();

      pendingRequestRef.current = request;
      return request;
    },
    [setState], // Only depend on setState, not on state values
  );

  return {
    fetchAvailableZones,
  };
};
